import { baseUrl, websiteShortCode, environment } from '../../environment';

export class Helper {

    websiteShortcodes = [
        'DS',
        'CG',
        'CX',
        'GT',
        'AG',
        'AK'
    ];

    pendingRequests = new Map();


    // ----- String Functionality -----

    // @ts-ignore
    includesSome = <T>(arr: string | T[], values: T[]) => values?.some(x => arr.includes(x));

    static readonly toRoute = (str: string): string => str.toLowerCase().trim().replaceAll(' ', '-');

    /**
     * Will change favicon to the given ShortCode.
     * @param shortCode ShortCode of the website.
     * @returns new favicon.
     */
    changeFavicon(shortCode: string) {
        let favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
        let shouldAppend = false;

        if (!favicon) {
            favicon = document.createElement('link');
            favicon.id = 'favicon';
            favicon.rel = 'icon';
            favicon.type = 'image/x-icon';
            favicon.sizes = '16x16';
            shouldAppend = true;
        };

        const withOrigin = (file: string, isCustom: boolean = false) => `${globalThis.location.origin}/${isCustom ? '' : 'favicons/'}${file}`;

        // Custom favicons for specific shortCodes
        const customFavicons: Record<string, { href: string; type: string }> = {
            AK: { href: withOrigin('images/favicon.png?v=2', true), type: 'image/png' },
            GT: { href: withOrigin('favicon-32x32.png', true), type: 'image/png' },
            AG: { href: withOrigin('favicon.ico', true), type: 'image/x-icon' }
        };

        if (customFavicons[shortCode]) {
            favicon.href = customFavicons[shortCode].href;
            favicon.type = customFavicons[shortCode].type;
            return;
        }

        const icons = {
            light: withOrigin('favicon.ico'),
            dark: withOrigin('favicon-dark.ico')
        };

        const darkModeQuery = globalThis.matchMedia('(prefers-color-scheme: dark)');

        const setIcon = (isDark: boolean) => {
            favicon.href = isDark ? icons.dark : icons.light;
        };

        setIcon(darkModeQuery.matches);

        darkModeQuery.addEventListener('change', (e) => setIcon(e.matches));

        if (shouldAppend) document.head.appendChild(favicon);
    };

    /**
     * Will return the base url of the website.
     * @function environmentLink - Returns the environment link.
     * @returns environment link.
     */
    get environmentLink() {
        const isResources = globalThis.location.hostname.includes('resources');
        if (isResources) {
            const projectEnv = import.meta.env.VITE_ENVIRONMENT;
            const host = globalThis.location.host;
            const parts = host.split('.');
            switch (projectEnv) {
                case 'dev':
                    return `https://${parts[0].replaceAll('-blogs', '')}.${parts[1]}.${parts[2]}`;
                case 'staging':
                    return `https://${parts[0].replaceAll('-blogs', '')}.${parts[1]}.${parts[2]}`;
                case 'production':
                    return `https://${parts[1]}.${parts[2]}`;
                default:
                    return 'Environment not found';
            }
        }
        else return `https://${globalThis.location.host}`;
    }

    debounce = (obj: any, fieldName: string, timeoutName: string, time: number = 2000, callback: Function = () => null) => {
        obj[fieldName] = true;
        if (obj[timeoutName]) {
            clearTimeout(obj[timeoutName]);
            obj[timeoutName] = null;
        }
        obj[timeoutName] ??= setTimeout(async () => {
            obj[timeoutName] = null;
            obj[`${fieldName}Inner`] = true;
            const response = await callback?.();
            obj[`${fieldName}Inner`] = false;
            if (!response) obj[fieldName] = false;
        }, time);
    };

    async handlePendingRequest(component, apiCall) {
        let isRequestPending = this.pendingRequests.get(component);
        if (isRequestPending === undefined) {
            isRequestPending = false;
            this.pendingRequests.set(component, isRequestPending);
        }

        while (isRequestPending) {
            await new Promise(resolve => setTimeout(resolve, 100));
            isRequestPending = this.pendingRequests.get(component);
        }

        this.pendingRequests.set(component, true);

        try {
            return await apiCall();
        } finally {
            this.pendingRequests.set(component, false);
        }
    }

    /**
     * Will the correct links for the footer from each website.
     * @function websiteFooterLinks - Returns the environment link.
     * @returns legalSectionsLinks and websiteSectionLinks.
     */

    get websiteFooterLinks() {
        let legalSectionDropdownLinks;
        let websiteSectionDropdownLinks;

        switch (websiteShortCode()) {
            case 'CG':
                legalSectionDropdownLinks = {
                    name: 'More', isDropdownMenu: true, showFilter: false, keyName: 'CG_FOOTER_LEGAL', menuItems: [
                        { name: 'Editorial Policy', routeName: 'editorial-policy' },
                        { name: 'Code of Ethics', routeName: 'code-of-ethics' },
                        { name: 'Complaints Policy', routeName: 'complaints-policy' },
                        { name: 'Disclaimer', routeName: 'disclaimer' },
                        { name: 'Accessibility', routeName: 'accessibility' },
                        { name: 'Modern Slavery Statement', routeName: 'modern-slavery-statement' },
                    ]
                };
                websiteSectionDropdownLinks = {
                    name: 'More', isDropdownMenu: true, showFilter: true, keyName:
                        'CG_FOOTER_BUY_SELL_SWAP', menuItems: [
                        { name: 'Buy Game Assets', routeName: 'disabled' },
                        { name: 'Buy Game Currency', routeName: 'currency' },
                        { name: 'Buy Game Items', routeName: 'items' },
                        { name: 'Buy Game Accounts', routeName: 'accounts' },
                        { name: 'Buy Game Services', routeName: 'services' },
                        { name: 'Buy Game Skins', routeName: 'skins' },
                        { name: 'Buy Game Gift Card', routeName: 'gift-cards' },
                        { name: 'Buy Game Unique Names', routeName: 'unique-names' },
                        { name: 'Sell Game Assets', routeName: 'disabled' },
                        { name: 'Sell Game Currency', routeName: 'sell/currency' },
                        { name: 'Sell Game Items', routeName: 'sell/items' },
                        { name: 'Sell Game Accounts', routeName: 'sell/accounts' },
                        { name: 'Sell Game Skins', routeName: 'sell/skins' },
                        { name: 'Swap Game Assets', routeName: 'disabled' },
                        //Temporarily disable per category as we don't have existing until we push new URL structure (only leaving main swap page)
                        { name: 'Swap Games', routeName: 'swap' },
                        /*{ name: 'Swap Game Currency', routeName: 'swap/currency' },
                        { name: 'Swap Game Items', routeName: 'swap/items' },
                        { name: 'Swap Game Skins', routeName: 'swap/skins' },
                        { name: 'Swap Game Unique Names', routeName: 'swap/unique-names' },*/
                    ]
                };

                return { legalSectionDropdownLinks, websiteSectionDropdownLinks };

            case 'CX':
                legalSectionDropdownLinks = {
                    name: 'More', isDropdownMenu: true, showFilter: true, keyName: 'CX_FOOTER_LEGAL', menuItems: [
                        { name: 'About Us', routeName: 'about-us' },
                        { name: 'Accessibility', routeName: 'accessibility' },
                        { name: 'Careers', routeName: 'careers' },
                        { name: 'Code of Ethics', routeName: 'code-of-ethics' },
                        { name: 'Complaints Policy', routeName: 'complaints-policy' },
                        { name: 'Cookies Policy', routeName: 'cookies' },
                        { name: 'Crypto Asset Statements', routeName: 'crypto-asset-statements' },
                        { name: 'CX Coin', routeName: 'cx-coin' },
                        { name: 'Disclaimer', routeName: 'disclaimer' },
                        { name: 'Editorial Policy', routeName: 'editorial-policy' },
                        { name: 'Modern Slavery Statement', routeName: 'modern-slavery-statement' },
                        { name: 'OTC', routeName: 'otc-trading-desk' },
                        { name: 'Escrow', routeName: 'escrow' },
                        { name: 'Sitemap', routeName: 'sitemap' },
                    ]
                };
                websiteSectionDropdownLinks = {
                    name: 'More',
                    isDropdownMenu: true,
                    showFilter: true,
                    keyName: 'CX_FOOTER_BUY_SELL_SWAP',
                    menuItems: [
                        { name: 'Buy Game Currency', routeName: 'currency' },
                        { name: 'Buy AUD to BTC', routeName: 'buy/aud-to-btc' },
                        { name: 'Buy BTC in Canada', routeName: 'buy/cad-to-btc' },
                        { name: 'Buy BTC in USA', routeName: 'buy/btc-to-usd' },
                        { name: 'Buy Crypto CX', routeName: 'buy' },
                        { name: 'Buy ETH in Canada', routeName: 'buy/cad-to-eth' },
                        { name: 'Buy LTC in USA', routeName: 'sell/usd-to-ltc' },
                        { name: 'Exchange BTC to ETH', routeName: 'swap/crypto/btc-to-eth' },
                        { name: 'Exchange BTC to LTC', routeName: 'swap/crypto/btc-to-ltc' },
                        { name: 'Exchange CAD to EUR', routeName: 'swap/fiat/cad-to-eur' },
                        { name: 'Exchange CAD to USD', routeName: 'swap/fiat/cad-to-usd' },
                        { name: 'Exchange ETH to BTC', routeName: 'swap/crypto/eth-to-btc' },
                        { name: 'Exchange EUR to CAD', routeName: 'swap/fiat/eur-to-cad' },
                        { name: 'Exchange LTC to BTC', routeName: 'swap/crypto/ltc-to-btc' },
                        { name: 'Exchange USD to CAD', routeName: 'swap/fiat/usd-to-cad' },
                        { name: 'Exchange USD to EUR', routeName: 'swap/fiat/usd-to-eur' },
                        { name: 'Sell', routeName: 'sell' },
                        { name: 'Sell BTC in Canada', routeName: 'sell/btc-to-cad' },
                        { name: 'Sell BTC in USA', routeName: 'sell/btc-to-usd' },
                        { name: 'Sell ETH in Canada', routeName: 'sell/eth-to-cad' },
                        { name: 'Sell ETH in USA', routeName: 'sell/eth-to-usd' },
                        { name: 'Sell LTC in USA', routeName: 'sell/ltc-to-usd' },
                        { name: 'Swap', routeName: 'swap' },
                        { name: 'Swap Crypto', routeName: 'swap/crypto' },
                    ]
                };
                return { legalSectionDropdownLinks, websiteSectionDropdownLinks };
            default:
                legalSectionDropdownLinks = {
                    name: '',
                    isDropdownMenu: false,
                    showFilter: false,
                    keyName: '',
                    menuItems: []
                };
                websiteSectionDropdownLinks = {
                    name: '',
                    isDropdownMenu: false,
                    showFilter: false,
                    keyName: '',
                    menuItems: []
                };
                return { legalSectionDropdownLinks, websiteSectionDropdownLinks };
        }
    }

    /**
     * Will return the logo by the website short code.
     * @function dynamicLogo - Returns the path from the logo for the website.
     * @returns string
     */

    get dynamicLogo() {
        const isResources = globalThis.location.hostname.includes('resources');
        const shortCode = websiteShortCode()?.toLowerCase();
        if (!shortCode) return 'UNDEFINED SHORTCODE';
        return `${isResources || environment() === 'local' ? '' : '/blog'}/logo/logo-${shortCode}.svg`;
    }

    handlePrerender404 = () => {
        const prerenderMeta = document.getElementById('prerender-404-status-code');
        if (prerenderMeta) return;
        const prerenderMetaTag = document.createElement('meta');
        prerenderMetaTag.setAttribute('id', 'prerender-404-status-code');
        prerenderMetaTag.setAttribute('name', 'prerender-status-code');
        prerenderMetaTag.setAttribute('content', '404');
        document.head.appendChild(prerenderMetaTag);
    };

    removePrerender404MetaTag = () => {
        document.getElementById('prerender-404-status-code')?.remove();
    };

    saveWindowLocalStorageValue(localStorage, value) {
        globalThis.localStorage[localStorage] = value;
    };

    addPrerenderMetaTagForRedirect = (url) => {
        this.removePrerenderMetaTagForRedirect();

        const metaPrerenderStatusCode = document.createElement('meta');
        metaPrerenderStatusCode.setAttribute('id', 'prerender-status-code');
        metaPrerenderStatusCode.setAttribute('name', 'prerender-status-code');
        metaPrerenderStatusCode.setAttribute('content', '301');

        const metaPrerenderHeader = document.createElement('meta');
        metaPrerenderHeader.setAttribute('id', 'prerender-header');
        metaPrerenderHeader.setAttribute('name', 'prerender-header');
        metaPrerenderHeader.setAttribute('content', `Location: ${baseUrl()?.slice(0, -1)}${url}`);

        document.head.appendChild(metaPrerenderStatusCode);
        document.head.appendChild(metaPrerenderHeader);
    };

    removePrerenderMetaTagForRedirect = () => {
        document.getElementById('prerender-header')?.remove();
        document.getElementById('prerender-status-code')?.remove();
    };

    checkIfResourcesDomain = () => globalThis.location.hostname.includes('resources');

    get hrefLang() {
        const language = JSON.parse(globalThis.localStorage.getItem('language') || '{}');
        return language?.language?.hrefLang || language?.hrefLang || 'en';
    }

    languageRoute(url: string, isRoute: boolean = false) {
        if (!url && url !== '') return;
        if (url.startsWith('http')) return url;
        if (['en', 'en-us'].includes(this.hrefLang.toLowerCase())) return url;
        return isRoute ? `${url}-${this.hrefLang.split('-').join('').toLowerCase()}` : `${this.hrefLang}/${url}`.replaceAll(/([^:]\/)\/+/g, '$1');
    }

    handleNavigationRequested(event: CustomEvent) {
        const link = event.detail?.link;
        if (link) {
            window.location.href = link;
        }
    }
}
